import { useState, type FormEvent } from 'react';
import { Pencil, Plus, Trash2, UserX } from 'lucide-react';
import { useSearchParams } from 'react-router';
import { formatDate, formatRelative, type Device } from '@findmy/shared';
import { useDeleteDevice, useDevices, useRenameDevice, useRevokeDevice } from '../api/queries';
import { BatteryIndicator, DeviceBadges, DeviceTypeIcon } from '../components/DeviceBits';
import { typeLabel } from '../lib/device';
import { Dialog } from '../components/Dialog';
import { PairingDialogContent } from '../components/PairingDialog';
import { Button, ErrorText, Spinner } from '../components/ui';
import { useNow } from '../lib/useNow';

type Confirm = { kind: 'revoke' | 'delete'; device: Device } | null;

function RenameForm({ device, onDone }: { device: Device; onDone: () => void }) {
  const rename = useRenameDevice();
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = String(new FormData(event.currentTarget).get('name')).trim();
    if (!name || name === device.name) return onDone();
    rename.mutate({ id: device.id, name }, { onSuccess: onDone });
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
      <input
        name="name"
        defaultValue={device.name}
        maxLength={60}
        autoFocus
        aria-label="Device name"
        className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-800"
      />
      <Button type="submit" variant="primary" disabled={rename.isPending}>
        Save
      </Button>
      <Button variant="ghost" onClick={onDone}>
        Cancel
      </Button>
      {rename.error && <ErrorText>{rename.error.message}</ErrorText>}
    </form>
  );
}

function DeviceRow({
  device,
  now,
  onConfirm,
}: {
  device: Device;
  now: number;
  onConfirm: (c: Confirm) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  return (
    <li className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-800">
        <DeviceTypeIcon type={device.type} className="h-5 w-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        {renaming ? (
          <RenameForm device={device} onDone={() => setRenaming(false)} />
        ) : (
          <p className="font-medium">{device.name}</p>
        )}
        <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-400">
          <span>{typeLabel[device.type]}</span>
          <span>Paired {formatDate(device.createdAt)}</span>
          <span>{device.lastSeenAt ? `Seen ${formatRelative(device.lastSeenAt, now)}` : 'Never seen'}</span>
          <BatteryIndicator pct={device.batteryPct} charging={device.isCharging} />
        </p>
        <div className="flex flex-wrap gap-1 empty:hidden">
          <DeviceBadges device={device} />
        </div>
      </div>
      {!renaming && (
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" onClick={() => setRenaming(true)}>
            <Pencil className="h-4 w-4" aria-hidden /> Rename
          </Button>
          {!device.revoked && (
            <Button variant="ghost" onClick={() => onConfirm({ kind: 'revoke', device })}>
              <UserX className="h-4 w-4" aria-hidden /> Remove
            </Button>
          )}
          <Button variant="ghostDanger" onClick={() => onConfirm({ kind: 'delete', device })}>
            <Trash2 className="h-4 w-4" aria-hidden /> Delete
          </Button>
        </div>
      )}
    </li>
  );
}

function ConfirmBody({ confirm, onDone }: { confirm: NonNullable<Confirm>; onDone: () => void }) {
  const revoke = useRevokeDevice();
  const remove = useDeleteDevice();
  const mutation = confirm.kind === 'revoke' ? revoke : remove;
  return (
    <div className="space-y-4">
      {confirm.kind === 'revoke' ? (
        <p className="text-sm">
          <strong>{confirm.device.name}</strong> will stop sharing its location straight away and has to be
          paired again to come back. Its location history is kept.
        </p>
      ) : (
        <p className="text-sm">
          <strong>{confirm.device.name}</strong> and <strong>all of its location history</strong> will be
          deleted. This can’t be undone.
        </p>
      )}
      {mutation.error && <ErrorText>{mutation.error.message}</ErrorText>}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button
          variant="danger"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate(confirm.device.id, { onSuccess: onDone })}
        >
          {confirm.kind === 'revoke' ? 'Remove device' : 'Delete device and history'}
        </Button>
      </div>
    </div>
  );
}

export function DevicesPage() {
  const { data: devices, error, isPending } = useDevices();
  const [params, setParams] = useSearchParams();
  const [confirm, setConfirm] = useState<Confirm>(null);
  const now = useNow();
  const pairing = params.get('pair') === '1';
  const setPairing = (open: boolean) => setParams(open ? { pair: '1' } : {});

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-6">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl font-semibold">Devices</h1>
          <Button variant="primary" onClick={() => setPairing(true)}>
            <Plus className="h-4 w-4" aria-hidden /> Add device
          </Button>
        </div>

        {isPending && <Spinner />}
        {error && <ErrorText>{error.message}</ErrorText>}
        {devices?.length === 0 && (
          <p className="rounded-xl bg-white p-6 text-sm text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:ring-slate-800">
            No devices yet. Choose <strong>Add device</strong>, then enter the code on the phone or computer
            you want to find.
          </p>
        )}
        {devices && devices.length > 0 && (
          <ul className="divide-y divide-slate-200 rounded-xl bg-white ring-1 ring-slate-200 dark:divide-slate-800 dark:bg-slate-900 dark:ring-slate-800">
            {devices.map((device) => (
              <DeviceRow key={device.id} device={device} now={now} onConfirm={setConfirm} />
            ))}
          </ul>
        )}
      </div>

      <Dialog open={pairing} onClose={() => setPairing(false)} title="Add a device">
        <PairingDialogContent devices={devices ?? []} onDone={() => setPairing(false)} />
      </Dialog>

      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm?.kind === 'delete' ? 'Delete device?' : 'Remove device?'}
      >
        {confirm && <ConfirmBody confirm={confirm} onDone={() => setConfirm(null)} />}
      </Dialog>
    </div>
  );
}
